import type {
  MetricsBySede,
  TotalByRiskLevelWithPercentage,
} from '@/domains/home/home.entity';
import type { Center, Day } from '@/domains/shared';
import {
  buildTotalByRiskLevelWithPercentage,
  reduceTotalsByRiskLevel,
} from '@/shared/utils';

/**
 * Use Case para calcular métricas de riesgo agrupadas por sede
 * Cumple SRP: Solo se encarga de agregar y calcular métricas por sede
 *
 * Responsabilidades:
 * - Filtrar métricas por sede específica (Linares/Parral)
 * - Opcionalmente filtrar por jornada activa
 * - Sumar totales de estudiantes por nivel de riesgo
 * - Calcular porcentajes de riesgo sobre el total de riesgos (no sobre estudiantes)
 * - Validar integridad de datos de entrada
 *
 * Encapsula la lógica de negocio de agregación y cálculo de porcentajes por sede
 * Los porcentajes siempre suman 100% porque se calculan sobre la suma total de riesgos
 * Opcionalmente puede filtrar por jornada activa para cálculos más específicos
 */
export class CalculateRiskBySedeUseCase {
  /**
   * Calcula totales y porcentajes de riesgo para una sede específica
   * Agrega métricas de la sede seleccionada, opcionalmente filtrando por jornada
   * Los porcentajes representan la distribución relativa entre niveles de riesgo
   * @param data - Array de métricas por sede que contienen datos de riesgo
   * @param sede - Sede a filtrar ('LI' para Linares, 'PA' para Parral)
   * @param activeJornada - Jornada activa opcional para filtrado adicional ('V' vespertino, 'D' diurno, null para todas)
   * @returns Objeto con totales y porcentajes de riesgo (alto, medio, bajo) que suman 100%
   * @throws Error si los datos no son válidos o la sede es inválida
   */
  execute(
    data: MetricsBySede[],
    sede: Center,
    activeJornada?: Day | null,
  ): TotalByRiskLevelWithPercentage {
    if (!Array.isArray(data)) {
      throw new Error('Los datos deben ser un array válido');
    }

    if (!sede || !['LI', 'PA'].includes(sede)) {
      throw new Error('La sede debe ser "LI" o "PA"');
    }

    let filteredData = data.filter((item) => item.sede === sede);

    if (activeJornada) {
      filteredData = filteredData.filter(
        (item) => item.jornada === activeJornada,
      );
    }

    const totals = reduceTotalsByRiskLevel(filteredData);

    return buildTotalByRiskLevelWithPercentage(totals);
  }
}
